import React, { useState, useRef, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiInstagram, FiX, FiHeart } from 'react-icons/fi';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

const InstagramFeed = () => {
  const scrollRef = useRef(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [continueShoppingLink, setContinueShoppingLink] = useState('/category/new-in');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get('/products');
        const allProducts = Array.isArray(res) ? res : (res.products || []);
        setProducts(allProducts.slice(0, 6));
      } catch (err) {
        console.error("Failed to fetch products for Instagram Feed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const res = await api.get('/settings/announcement');
        if (res.continueShoppingLink) {
          setContinueShoppingLink(res.continueShoppingLink);
        }
      } catch (err) {
        console.error('Failed to fetch continue shopping link', err);
      }
    };
    fetchLink();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const openModal = (index) => {
    setCurrentIndex(index);
    setSelectedPost(products[index]);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedPost(null);
    document.body.style.overflow = 'unset';
  };

  const handleNext = (e) => {
    e.stopPropagation();
    const nextIndex = (currentIndex + 1) % products.length;
    setCurrentIndex(nextIndex);
    setSelectedPost(products[nextIndex]);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    const prevIndex = (currentIndex - 1 + products.length) % products.length;
    setCurrentIndex(prevIndex);
    setSelectedPost(products[prevIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight' && selectedPost) handleNext(e);
      if (e.key === 'ArrowLeft' && selectedPost) handlePrev(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPost, currentIndex]);

  if (loading) {
    return (
      <div className="w-full bg-white py-16 flex justify-center">
        <LoadingSpinner text="Loading products..." />
      </div>
    );
  }

  if (products.length === 0) return null;

  return (
    <div className="w-full bg-white py-16">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8">
        
        <div className="flex flex-col items-center mb-10">
          <div className="w-28 h-28 md:w-36 md:h-36 rounded-full border-[8px] border-white shadow-lg overflow-hidden bg-white mb-4 p-1">
            <img src="/logo.png" alt="Brand Logo" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-3xl md:text-4xl font-light tracking-[0.2em] text-gray-900 uppercase">
            INSTAGRAM FEED
          </h2>
          <div className="flex items-center justify-center gap-2 mt-3">
            <FiInstagram className="text-gray-700" size={20} />
            <span className="text-sm font-medium text-gray-600 tracking-wide">3M FOLLOWERS</span>
          </div>
        </div>

        <div className="relative group">
          <button 
            onClick={() => scroll('left')} 
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all opacity-70 hover:opacity-100"
          >
            <FiChevronLeft size={24} className="text-gray-800" />
          </button>
          
          <div 
            ref={scrollRef} 
            className="flex overflow-x-auto gap-5 scroll-smooth hide-scrollbar pb-4"
          >
            {products.map((product, index) => (
              <div 
                key={product._id} 
                className="flex-shrink-0 w-[180px] sm:w-[240px] md:w-[280px] lg:w-[320px] relative group/item cursor-pointer"
                onClick={() => openModal(index)}
              >
                <div className="relative overflow-hidden aspect-square">
                  <img 
                    src={product.images?.[0] || 'https://via.placeholder.com/400'} 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-105" 
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-6">
                    <div className="flex items-center gap-1 text-white">
                      <FiInstagram size={20} />
                      <span className="text-sm font-medium">{(product.numReviews || 0)}+</span>
                    </div>
                    <div className="flex items-center gap-1 text-white">
                      <FiHeart size={20} />
                      <span className="text-sm font-medium">{product.rating || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <button 
            onClick={() => scroll('right')} 
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all opacity-70 hover:opacity-100"
          >
            <FiChevronRight size={24} className="text-gray-800" />
          </button>
        </div>

        <div className="flex justify-center mt-10">
          <a href={continueShoppingLink} className="px-8 py-3 border border-gray-300 text-gray-700 font-medium text-sm hover:text-black hover:border-black transition-all uppercase tracking-widest">
            SEE MORE
          </a>
        </div>

      </div>

      {selectedPost && createPortal(
        <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div 
            className="bg-white w-full max-w-[1200px] h-[90vh] rounded-lg overflow-hidden flex flex-col md:flex-row relative animate-fade-in shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 z-20 text-white md:text-gray-800 hover:text-gray-900 transition-colors bg-black/30 md:bg-transparent rounded-full p-2 backdrop-blur-sm"
            >
              <FiX size={28} />
            </button>

            <div className="w-full md:w-[65%] h-[50vh] md:h-full bg-gray-100 relative">
              <img 
                src={selectedPost.images?.[0] || 'https://via.placeholder.com/400'} 
                alt={selectedPost.name} 
                className="w-full h-full object-cover" 
              />
              
              <button 
                onClick={handlePrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-colors z-10"
              >
                <FiChevronLeft size={24} className="text-gray-800" />
              </button>
              <button 
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-colors z-10"
              >
                <FiChevronRight size={24} className="text-gray-800" />
              </button>
            </div>

            <div className="w-full md:w-[35%] h-[50vh] md:h-full overflow-y-auto p-4 md:p-6 border-t md:border-t-0 md:border-l border-gray-200 flex flex-col">
              <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                <img 
                  src="/logo.png" 
                  alt="Profile" 
                  className="w-8 h-8 rounded-full object-cover" 
                />
                <div className="flex flex-col">
                  <span className="font-semibold text-sm text-gray-900">arshivastra</span>
                  <span className="text-xs text-gray-500">Follow</span>
                </div>
              </div>

              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">arshivastra</span>
                  <span className="text-sm text-gray-700 whitespace-pre-line">
                    {selectedPost.description || selectedPost.shortDescription || "Premium product available at Arshivastra."}
                  </span>
                </div>

                <div className="mt-2 text-sm font-medium">
                  {selectedPost.originalPrice && selectedPost.originalPrice > selectedPost.price ? (
                    <div className="flex items-center gap-3">
                      <span className="text-gray-900">Rs. {selectedPost.price.toLocaleString()}</span>
                      <span className="text-gray-400 line-through">Rs. {selectedPost.originalPrice.toLocaleString()}</span>
                      <span className="text-[var(--primary-color)] text-xs font-bold">{Math.round(((selectedPost.originalPrice - selectedPost.price) / selectedPost.originalPrice) * 100)}% OFF</span>
                    </div>
                  ) : (
                    <span className="text-gray-900">Rs. {selectedPost.price.toLocaleString()}</span>
                  )}
                </div>

                <div className="text-sm text-blue-500 whitespace-pre-line">
                  #{selectedPost.category?.name || selectedPost.category || 'Product'} #{selectedPost.brand || 'Arshivastra'}
                  {selectedPost.tags?.map(tag => ` #${tag}`).join('')}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 text-right">
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InstagramFeed;